import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_KEY = 'cookieConsent';
const CONSENT_TIMESTAMP_KEY = 'cookieConsentTimestamp';
const CONSENT_EXPIRY_DAYS = 180;

export function pushConsentUpdate(prefs: ConsentPreferences) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      personalization_storage: prefs.analytics ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: prefs.analytics ? 'granted' : 'denied',
    consent_marketing: prefs.marketing ? 'granted' : 'denied',
  });
}

function isConsentExpired(): boolean {
  const ts = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
  if (!ts) return true;
  const saved = parseInt(ts, 10);
  return Date.now() - saved > CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
}

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const open = () => {
      setShowPreferences(true);
      setIsVisible(true);
    };
    window.addEventListener('sb-open-consent', open);

    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw || isConsentExpired()) {
      setIsVisible(true);
    } else {
      try {
        const saved: ConsentPreferences = JSON.parse(raw);
        setPreferences({ ...saved, necessary: true });
      } catch {
        setIsVisible(true);
      }
    }

    return () => window.removeEventListener('sb-open-consent', open);
  }, []);

  const persist = useCallback((prefs: ConsentPreferences) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString());
    pushConsentUpdate(prefs);
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 z-[9999] mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-4 shadow-xl md:bottom-4 md:p-5">
      {!showPreferences ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm leading-relaxed text-gray-700">
            Usiamo cookie tecnici. Quelli di misurazione e pubblicitari (Google Ads) partono solo se accetti.{' '}
            <Link to="/cookie-policy" className="font-medium text-primary hover:underline">
              Cookie Policy
            </Link>
          </p>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => setShowPreferences(true)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              Personalizza
            </button>
            <button
              type="button"
              onClick={() => persist({ necessary: true, analytics: false, marketing: false })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Rifiuta
            </button>
            <button
              type="button"
              onClick={() => persist({ necessary: true, analytics: true, marketing: true })}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            >
              Accetta
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="mb-3 text-sm font-semibold text-gray-900">Preferenze cookie</p>
          <div className="mb-4 space-y-2 text-sm text-gray-700">
            <label className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
              <span>Necessari, sempre attivi</span>
              <input type="checkbox" checked readOnly className="h-4 w-4" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
              <span>Misurazione (analytics)</span>
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                className="h-4 w-4"
              />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
              <span>Pubblicità (Google Ads)</span>
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={() => setPreferences((p) => ({ ...p, marketing: !p.marketing }))}
                className="h-4 w-4"
              />
            </label>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setShowPreferences(false)}
              className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            >
              Indietro
            </button>
            <button
              type="button"
              onClick={() => persist({ necessary: true, analytics: false, marketing: false })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800"
            >
              Rifiuta
            </button>
            <button
              type="button"
              onClick={() => persist(preferences)}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              Salva
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieBanner;
